import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { Trophy, ChevronRight, CalendarDays, Activity, Sparkles, Lock, Clock, History, ChevronDown, X, Check, Smartphone, Monitor, Globe2 } from "lucide-react";
import RelPill from "../RelPill";
import SkinnedProfileCard from "../SkinnedProfileCard";
import PushOptInCard from "../PushOptInCard";
import CtfBanner from "../../components/CtfBanner";

/**
 * V2 / Phase 1 — "My League" tab.
 * Mobile-first dark UI matching the new design mock.
 * Data sources reuse existing v1 endpoints unchanged.
 *
 * Iter 37 — League switcher: tap the season name in the hero card to open a
 * sheet listing every league the user is in (or every league for admin).
 * Selecting "Mine" snaps back to /leagues/active.
 */
export default function MyLeagueV2() {
  const { user } = useAuth();
  const { t } = useT();
  const [defaultLeague, setDefaultLeague] = useState(null);    // /leagues/active result
  const [selectedLeague, setSelectedLeague] = useState(null);  // currently displayed league
  const [allLeagues, setAllLeagues] = useState([]);            // picker options
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [participation, setParticipation] = useState(null);    // {coop,proclubs,friendlies}

  // Initial load — default league + the user's leagues + ALL their matches +
  // a probe of every other mode (coop/proclubs/friendlies/wctourney) so we
  // can render "Your active modes" tiles for only the ones the user is in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [actR, lgR, msR, coopR, pcR, frR, wcMR, wcCR] = await Promise.all([
          api.get("/leagues/active").catch(() => ({ data: null })),
          api.get("/leagues").catch(() => ({ data: [] })),
          api.get("/matches/my/all").catch(() => ({ data: [] })),
          api.get("/coop/me").catch(() => ({ data: null })),
          api.get("/proclubs/me").catch(() => ({ data: null })),
          api.get("/friendlies/me").catch(() => ({ data: null })),
          api.get("/wctourney/me?platform=mobile").catch(() => ({ data: null })),
          api.get("/wctourney/me?platform=console").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const act = actR.data || null;
        setDefaultLeague(act);
        setSelectedLeague(act);
        setAllLeagues(Array.isArray(lgR.data) ? lgR.data : []);
        setMatches(Array.isArray(msR.data) ? msR.data : []);
        setParticipation({
          coop:        coopR.data || null,
          proclubs:    pcR.data || null,
          friendlies:  frR.data || null,
          wct_mobile:  wcMR.data || null,
          wct_console: wcCR.data || null,
        });
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Whenever the selected league changes, refresh standings for it.
  useEffect(() => {
    let cancelled = false;
    if (!selectedLeague?.id) { setStandings([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${selectedLeague.id}/standings`);
        if (!cancelled) setStandings(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setStandings([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedLeague?.id]);

  const me = standings.find((s) => s.user_id === user?.id);
  const myRank = me ? standings.findIndex((s) => s.user_id === user?.id) + 1 : null;

  // Match split — filtered to the selected league.
  const { upcoming, recent } = useMemo(() => {
    const up = [];
    const rec = [];
    const lid = selectedLeague?.id;
    for (const m of matches || []) {
      if (lid && m.league_id !== lid) continue;
      if (m.status === "confirmed") rec.push(m);
      else up.push(m);
    }
    rec.sort((a, b) => (b.confirmed_at || "").localeCompare(a.confirmed_at || ""));
    return { upcoming: up, recent: rec };
  }, [matches, selectedLeague?.id]);

  const nextMatch = upcoming[0];
  const opponent = nextMatch ? (nextMatch.home_id === user?.id ? nextMatch.away : nextMatch.home) : null;

  // Picker options — sorted: default league first, then most recent first.
  const pickerOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    if (defaultLeague?.id) { out.push(defaultLeague); seen.add(defaultLeague.id); }
    for (const lg of allLeagues) {
      if (lg.id && !seen.has(lg.id)) { out.push(lg); seen.add(lg.id); }
    }
    return out;
  }, [defaultLeague, allLeagues]);

  if (loading) return <div className="p-6 text-center text-[#52525B] text-sm" data-testid="v2-loading">{t("v2.shell.loading")}</div>;

  const active = selectedLeague;
  const isViewingMine = !!(defaultLeague && active && defaultLeague.id === active.id);
  // What other modes is the user currently in?
  const inCoop       = !!(participation?.coop?.opted_in && participation?.coop?.profile);
  const inProClubs   = !!(participation?.proclubs?.team);
  const inFriendlies = !!(participation?.friendlies?.active_team || participation?.friendlies?.pool);
  // WC 48-team tournament revived iter 68 — show widget if user has a slot in either pool.
  const inWcMobile   = !!(participation?.wct_mobile?.tournament);
  const inWcConsole  = !!(participation?.wct_console?.tournament);

  return (
    <div className="space-y-5 px-5 pt-5" data-testid="v2-myleague">
      {/* Greeting */}
      <section data-testid="v2-greeting">
        <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A5B4FC]">{t("v2.my.welcome")}</div>
        <h1 className="text-2xl font-bold text-white mt-0.5">{user?.club_name || user?.username || "Player"}</h1>
      </section>

      {/* iter 96 — "Collect The Flags is live" announcement (auto-shows when open) */}
      <CtfBanner to="/v2/wc2026/ctf" />

      {/* My league hero card (skinned when active_skin set) */}
      {active ? (
        <SkinnedProfileCard
          user={user}
          league={active}
          me={me}
          myRank={myRank}
          isViewingMine={isViewingMine}
          onPickerOpen={() => setPickerOpen(true)}
          skinId={user?.active_skin}
        />
      ) : (
        <EmptyLeagueCard />
      )}

      {/* League widget — same tile that appears under the "Discover" tab so
          a player registered in a league has the league's overview card
          (status pill, fillness bar, platform icon) right inside My League.
          Links to the league hub. */}
      {active && <LeagueWidget league={active} />}

      {/* Push notifications opt-in (self-hides if granted/denied/dismissed) */}
      <PushOptInCard />

      {/* Next match */}
      <section data-testid="v2-next-match">
        <SectionHeader title="Next Match" actionLabel="All matches" actionTo="/v2/my-matches" />
        {nextMatch ? <NextMatchCard m={nextMatch} opponent={opponent} userId={user?.id} /> : <EmptyCard text="No matches scheduled" icon={CalendarDays} />}
      </section>

      {/* Scheduled (upcoming after the next one) */}
      {upcoming.length > 1 && (
        <section data-testid="v2-scheduled">
          <SectionHeader title="Scheduled" actionLabel="See all" actionTo="/v2/my-matches" />
          <div className="space-y-2">
            {upcoming.slice(1, 5).map((m) => (
              <MatchRow key={m.id} m={m} userId={user?.id} variant="upcoming" />
            ))}
          </div>
        </section>
      )}

      {/* Recent results */}
      {recent.length > 0 && (
        <section data-testid="v2-recent-results">
          <SectionHeader title="Recent Results" actionLabel="See all" actionTo="/v2/my-matches" />
          <div className="space-y-2">
            {recent.slice(0, 5).map((m) => (
              <MatchRow key={m.id} m={m} userId={user?.id} variant="result" />
            ))}
          </div>
        </section>
      )}

      {/* Performance card (uses existing standings row) */}
      {me && (
        <section data-testid="v2-perf">
          <SectionHeader title="Your Form" />
          <div className="grid grid-cols-4 gap-2">
            <PerfCell label="P" value={me.played} accent="#A1A1AA" />
            <PerfCell label="W" value={me.won} accent="#10B981" />
            <PerfCell label="D" value={me.drawn} accent="#F59E0B" />
            <PerfCell label="L" value={me.lost} accent="#EF4444" />
          </div>
        </section>
      )}

      {/* UFL Mobile widget moved to /v2/explore (iter 88). */}

      {/* Your active competitions — Discover-style widgets, only shown for
          modes the user actually participates in. Tap any card to open the
          full mode page. Visual language mirrors the Discover tab. */}
      {(inFriendlies || inProClubs || inCoop || inWcMobile || inWcConsole) && (
        <section data-testid="v2-my-modes" className="space-y-2.5">
          <SectionHeader title="Your Other Competitions" actionLabel="Browse all" actionTo="/v2/explore" />
          {inWcMobile && (
            <WcTourneyWidget state={participation.wct_mobile} platform="mobile" />
          )}
          {inWcConsole && (
            <WcTourneyWidget state={participation.wct_console} platform="console" />
          )}
          {inFriendlies && (
            <FriendliesWidget data={participation.friendlies} />
          )}
          {inProClubs && (
            <ProClubsWidget data={participation.proclubs} />
          )}
          {inCoop && (
            <CoopWidget data={participation.coop} />
          )}
        </section>
      )}

      {pickerOpen && (
        <LeaguePickerSheet
          options={pickerOptions}
          selectedId={selectedLeague?.id}
          defaultId={defaultLeague?.id}
          onSelect={(lg) => { setSelectedLeague(lg); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function ModeRow({ testId, to, icon: Icon, accent, title, subtitle }) {
  // Retained for backwards compatibility; rich widgets below are preferred.
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-3 bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl px-3 py-3 transition-colors"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}22`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate">{title}</div>
        <div className="text-[11px] text-[#71717A] truncate">{subtitle}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#52525B]" />
    </Link>
  );
}

/* Rich Discover-style competition widgets — mirror the visual language of
   the Explore tab so a player has a consistent feel between "browse all" and
   "my competitions". Each shows live status + a contextual subtitle pulled
   from the user's participation snapshot. */

function WcTourneyWidget({ state, platform }) {
  const tn = state?.tournament;
  const myTeam = state?.my_team;
  const myGroup = state?.my_group;
  const platformLabel = platform === "console" ? "Console" : "Mobile";
  const status = tn?.status || "forming";
  const filled = tn?.filled || 0;
  const fullness = Math.min(100, Math.round((filled / 48) * 100));
  return (
    <Link
      to={`/v2/world-cup-tourney/${platform}`}
      data-testid={`v2-my-modes-wctourney-${platform}`}
      className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#FFD700]/50 hover:ring-[#FFD700] transition-shadow group"
    >
      <div className="bg-gradient-to-br from-[#FFD700]/20 via-[#1E3A8A]/40 to-[#06061A] h-44 flex items-center px-5 relative">
        <div className="text-7xl mr-4 group-hover:scale-110 transition-transform">{myTeam?.flag || "🏆"}</div>
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#FFD700] text-[#06061A] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-[#06061A] animate-pulse" /> Live · {status.replace(/_/g, " ")}
          </span>
          <h4 className="mt-2 text-lg font-extrabold text-white drop-shadow leading-tight">World Cup · {platformLabel}</h4>
          <p className="text-[11px] text-white/85 mt-1 drop-shadow">
            {myTeam ? `${myTeam.name} · Group ${myGroup}` : "Drawing in progress…"}
          </p>
          <div className="mt-2 flex items-center justify-between text-[10px] text-white/80">
            <span className="font-mono uppercase tracking-widest">{filled}/48 slots</span>
            <span className="font-mono uppercase tracking-widest">{fullness}% full</span>
          </div>
          <div className="h-1 bg-black/40 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-[#FFD700]" style={{ width: `${fullness}%` }} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function FriendliesWidget({ data }) {
  const team = data?.active_team?.country;
  const pool = (data?.pool || "mobile").toLowerCase();
  return (
    <Link
      to={`/v2/friendlies/${pool}`}
      data-testid="v2-my-modes-friendlies"
      className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#60A5FA]/50 hover:ring-[#60A5FA] transition-shadow group"
    >
      <div className="bg-gradient-to-br from-[#0c1f4d] via-[#1a3a8c] to-[#040a1e] h-44 flex items-center px-5 relative">
        <div className="text-7xl mr-4 group-hover:scale-110 transition-transform">{team?.flag || "🌍"}</div>
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#60A5FA] text-[#040a1e] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-[#040a1e]" /> Live
          </span>
          <h4 className="mt-2 text-lg font-extrabold text-white drop-shadow leading-tight">International Friendlies</h4>
          <p className="text-[11px] text-white/85 mt-1 drop-shadow">
            {team?.name ? `${team.name} · ${pool.toUpperCase()}` : `${pool.toUpperCase()} pool member`}
          </p>
        </div>
        <Globe2 className="absolute right-4 top-4 w-4 h-4 text-white/40" />
      </div>
    </Link>
  );
}

function ProClubsWidget({ data }) {
  const team = data?.team || {};
  const size = team.size ?? (team.player_count ?? 0);
  const isCaptain = !!data?.is_captain;
  const isDeputy = !!data?.is_deputy;
  const role = isCaptain ? "Captain" : isDeputy ? "Deputy" : "Player";
  return (
    <Link
      to="/v2/proclubs"
      data-testid="v2-my-modes-proclubs"
      className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#00FF87]/60 hover:ring-[#00FF87] transition-shadow group"
    >
      <img
        src="/wc/proclubs.png"
        alt="Pro Clubs"
        className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#06120A]/95 via-[#06120A]/40 to-transparent" />
      <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
        <span className="w-1.5 h-1.5 rounded-full bg-[#06120A]" /> Live · {role}
      </span>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <h4 className="text-lg font-extrabold text-white drop-shadow leading-tight">{team.name || "Pro Clubs Team"}</h4>
        <p className="text-[11px] text-white/85 mt-1 drop-shadow">EA FC Pro Clubs · {size}/10 players</p>
      </div>
    </Link>
  );
}

function CoopWidget({ data }) {
  const profile = data?.profile || {};
  const games = profile.games_enriched?.length || 0;
  const plat = profile.play_platform || "—";
  const rel = profile.reliability_score != null ? `${Math.round(profile.reliability_score * 20)}% REL` : null;
  return (
    <Link
      to="/v2/coop"
      data-testid="v2-my-modes-coop"
      className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#A78BFA]/50 hover:ring-[#A78BFA] transition-shadow group"
    >
      <div className="bg-gradient-to-br from-[#1a0a3e] via-[#2a0e6f] to-[#0a0420] h-44 flex items-center px-5 relative">
        <div className="text-7xl mr-4 group-hover:scale-110 transition-transform">🎮</div>
        <div className="flex-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#A78BFA] text-[#06061A] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-[#06061A]" /> Active
          </span>
          <h4 className="mt-2 text-lg font-extrabold text-white drop-shadow leading-tight">Co-op Matchmaking</h4>
          <p className="text-[11px] text-white/85 mt-1 drop-shadow">{games} games · {plat}{rel ? ` · ${rel}` : ""}</p>
        </div>
        <div className="absolute right-4 top-4 text-[10px] font-mono uppercase tracking-widest text-white/40">// AI</div>
      </div>
    </Link>
  );
}

function LeaguePickerSheet({ options, selectedId, defaultId, onSelect, onClose }) {
  const { t } = useT();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      data-testid="v2-league-picker-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[480px] bg-[#0F0F18] border-t border-[#27272F] rounded-t-3xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white">{t("v2.my.picker.title")}</h3>
          <button onClick={onClose} data-testid="v2-league-picker-close" className="text-[#71717A] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {options.length === 0 ? (
          <div className="text-sm text-[#71717A] text-center py-6">{t("v2.my.picker.empty")}</div>
        ) : (
          <div className="space-y-1.5">
            {options.map((lg) => {
              const isSelected = lg.id === selectedId;
              const isMine = lg.id === defaultId;
              return (
                <button
                  key={lg.id}
                  type="button"
                  onClick={() => onSelect(lg)}
                  data-testid={`v2-league-picker-option-${lg.id}`}
                  className={`w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-colors text-left border ${
                    isSelected ? "bg-[#7C3AED]/15 border-[#7C3AED]/50" : "bg-[#12121C] border-[#1F1F2A] hover:bg-[#161620]"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{lg.name || "—"}</span>
                      {isMine && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] font-bold">{t("v2.my.picker.mine")}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#71717A] mt-0.5 capitalize">
                      {(lg.platform || "mobile")} · {lg.player_count ?? 0}/{lg.capacity || 20} · {lg.status || "—"}
                    </div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-[#A5B4FC] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl px-3 py-2.5" data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/70">{label}</div>
      <div className="text-lg font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}

function PerfCell({ label, value, accent }) {
  return (
    <div className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl px-3 py-3 text-center">
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{label}</div>
      <div className="text-xl font-bold text-white mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function SectionHeader({ title, actionLabel, actionTo }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h3 className="text-sm font-bold text-white">{title}</h3>
      {actionLabel && actionTo && (
        <Link to={actionTo} className="text-xs font-medium text-[#A5B4FC] hover:text-white transition-colors flex items-center gap-0.5">
          {actionLabel} <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

function NextMatchCard({ m, opponent, userId }) {
  const { t } = useT();
  const oppName = opponent?.username || "Opponent";
  return (
    <Link
      to={`/v2/matches/${m.id}`}
      data-testid={`v2-next-match-${m.id}`}
      className="block bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl p-4 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-white text-sm">
            {oppName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{t("v2.my.vs", { opp: oppName })}</div>
            <div className="text-[11px] text-[#71717A] mt-0.5">
              {m.scheduled_at ? `${m.scheduled_at} · ` : (m.round != null ? `${t("v2.my.round", { n: m.round + 1 })} · ` : "")}{(m.status || "scheduled").replace(/_/g, " ")}
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#52525B]" />
      </div>
    </Link>
  );
}

function MatchRow({ m, userId, variant }) {
  const { t } = useT();
  const isHome = m.home_id === userId;
  const opp = isHome ? m.away : m.home;
  const oppName = opp?.username || "Opponent";

  if (variant === "result") {
    const myScore = isHome ? m.confirmed_score_home : m.confirmed_score_away;
    const oppScore = isHome ? m.confirmed_score_away : m.confirmed_score_home;
    let outcome = "D"; let oc = "#F59E0B"; let ob = "#F59E0B33";
    if (myScore > oppScore) { outcome = "W"; oc = "#10B981"; ob = "#10B98133"; }
    else if (myScore < oppScore) { outcome = "L"; oc = "#EF4444"; ob = "#EF444433"; }
    return (
      <Link
        to={`/v2/matches/${m.id}`}
        data-testid={`v2-result-${m.id}`}
        className="flex items-center gap-3 bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl px-3 py-2.5 transition-colors"
      >
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ backgroundColor: ob, color: oc }}
        >
          {outcome}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">{t("v2.my.vs", { opp: oppName })}</div>
          <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-1">
            <History className="w-3 h-3" />
            {m.confirmed_at ? new Date(m.confirmed_at).toLocaleDateString() : (m.scheduled_at || "—")}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-white tabular-nums">{myScore}–{oppScore}</div>
          {m.auto_drawn && <div className="text-[9px] uppercase tracking-wider text-[#71717A]">{t("v2.my.auto")}</div>}
        </div>
      </Link>
    );
  }

  // upcoming
  return (
    <Link
      to={`/v2/matches/${m.id}`}
      data-testid={`v2-upcoming-${m.id}`}
      className="flex items-center gap-3 bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl px-3 py-2.5 transition-colors"
    >
      <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#7C3AED]/20 text-[#A5B4FC] flex-shrink-0">
        <Clock className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">{t("v2.my.vs", { opp: oppName })}</div>
        <div className="text-[11px] text-[#71717A] mt-0.5">
          {m.scheduled_at || (m.round != null ? t("v2.my.round", { n: m.round + 1 }) : t("v2.my.tbd"))} · {(m.status || "pending").replace(/_/g, " ")}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#52525B]" />
    </Link>
  );
}

function EmptyCard({ text, icon: Icon = Lock }) {
  return (
    <div className="bg-[#12121C] border border-dashed border-[#1F1F2A] rounded-2xl p-6 flex flex-col items-center text-center">
      <Icon className="w-6 h-6 text-[#52525B] mb-2" />
      <div className="text-sm text-[#71717A]">{text}</div>
    </div>
  );
}

function EmptyLeagueCard() {
  const { t } = useT();
  return (
    <div data-testid="v2-no-league" className="rounded-3xl p-6 bg-gradient-to-br from-[#1F1F2A] to-[#12121C] border border-[#27272F]">
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.my.empty.title")}</div>
      <h2 className="text-lg font-bold text-white mt-1">{t("v2.my.empty.title")}</h2>
      <p className="text-sm text-[#A1A1AA] mt-1.5">{t("v2.my.empty.body")}</p>
      <Link
        to="/v2/explore"
        data-testid="v2-go-explore"
        className="mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] hover:opacity-90 rounded-2xl px-4 py-3 text-sm font-semibold text-white transition-opacity"
      >
        {t("v2.my.empty.cta")} <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function QuickLink({ icon: Icon, label, href, accent, testId }) {
  return (
    <Link
      to={href}
      data-testid={testId}
      className="bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl p-4 flex items-center gap-3 transition-colors"
    >
      <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}20`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-sm font-semibold text-white">{label}</span>
    </Link>
  );
}

/* League widget — mirrors the "Discover" tab's LeagueCard so a player who is
   registered in a master league always has the same headline tile (live
   pill, fillness bar, platform identity) inside My League. Clickable —
   routes straight to /v2/leagues/:id (the league hub). */
function LeagueWidget({ league }) {
  if (!league) return null;
  const platform = (league.platform || "mobile").toLowerCase();
  const isMobile = platform === "mobile";
  const Icon = isMobile ? Smartphone : Monitor;
  const gradient = isMobile ? "from-[#7C3AED] to-[#3B82F6]" : "from-[#10B981] to-[#3B82F6]";
  const badgeColor = isMobile ? "#7C3AED" : "#10B981";
  const label = isMobile ? "Mobile · eFootball" : "Console · PS5 / Steam";
  const capacity = league.capacity || 20;
  const filled = league.player_count ?? 0;
  const fullness = Math.min(100, Math.round((filled / capacity) * 100));
  return (
    <Link
      to={`/v2/leagues/${league.id}`}
      data-testid="v2-my-league-widget"
      className="relative block overflow-hidden rounded-3xl border border-[#27272F] bg-[#12121C] hover:bg-[#161620] transition-colors"
    >
      <div className="flex items-stretch">
        <div className="relative w-24 flex-shrink-0">
          <img src="/byol-cover.png" alt={label} className="w-24 h-full object-cover" />
          <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-50`} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="w-8 h-8 text-white drop-shadow-lg" />
          </div>
        </div>
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: badgeColor }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: badgeColor }} /> Live · {league.status || "active"}
            </span>
          </div>
          <h4 className="text-sm font-bold text-white mt-1 truncate">{league.name || label}</h4>
          <p className="text-[11px] text-[#71717A] mt-0.5 leading-tight">Online Master League · {capacity} players · Double round-robin</p>
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-[#A1A1AA] font-semibold">{filled}/{capacity}</span>
              <span className="text-[#71717A]">{fullness}% filled</span>
            </div>
            <div className="h-1 bg-[#1F1F2A] rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${gradient} rounded-full`} style={{ width: `${fullness}%` }} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* iter 88 — UflMobileWidget removed from MyLeague (moved to Explore page). */


